import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BriefcaseIcon, HashIcon, UserIcon, FileTextIcon, PlayIcon, CheckCircleIcon, ArrowUUpLeftIcon, GitDiffIcon, TrashIcon } from "@phosphor-icons/react";

const STATUS = {
  new: { label: "New", cls: "border-cyan-500/40 bg-cyan-500/10 text-cyan-300" },
  accepted: { label: "Accepted", cls: "border-sky-500/40 bg-sky-500/10 text-sky-300" },
  in_progress: { label: "In progress", cls: "border-amber-500/40 bg-amber-500/10 text-amber-300" },
  config_generated: { label: "Config generated", cls: "border-violet-500/40 bg-violet-500/10 text-violet-300" },
  tested: { label: "Tested", cls: "border-teal-500/40 bg-teal-500/10 text-teal-300" },
  complete: { label: "Complete", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" },
  open: { label: "Open", cls: "border-cyan-500/40 bg-cyan-500/10 text-cyan-300" },
};

const ACTION = {
  new: "Accept & start", accepted: "Open", in_progress: "Open",
  config_generated: "Review & sign off",
};

export const JobsDialog = ({ open, onOpenChange, onStartJob, onStartCO, onLoad }) => {
  const [items, setItems] = useState([]);
  const [cos, setCos] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: jobs }, { data: changeOrders }] = await Promise.all([
        api.get("/jobs"),
        api.get("/change-orders"),
      ]);
      setItems(jobs);
      setCos(changeOrders);
    } catch { toast.error("Failed to load jobs"); }
    setLoading(false);
  };
  useEffect(() => { if (open) load(); }, [open]);

  const handleOpen = async (j) => {
    if (j.status === "new") {
      try { await api.post(`/jobs/${j.id}/accept`); }
      catch (e) { toast.error(e?.response?.data?.detail || "Failed to accept job"); return; }
      onStartJob(j);
      onOpenChange(false);
      return;
    }
    // already built -> load the existing generation for review & sign-off
    if (j.gen_id) {
      try {
        const { data } = await api.get(`/history/${j.gen_id}`);
        onLoad(data);
        onOpenChange(false);
        return;
      } catch { toast.error("Failed to load generated config"); return; }
    }
    // accepted but not yet built -> continue the design
    onStartJob(j);
    onOpenChange(false);
  };

  const handleReopen = async (e, j) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/jobs/${j.id}/reopen`, {});
      toast.success(`Sent back to “${data.status}”`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to reopen"); }
  };

  const canReopen = (s) => ["in_progress", "config_generated", "complete"].includes(s);

  const handleDismiss = async (e, j) => {
    e.stopPropagation();
    if (!window.confirm(`Remove job ${j.ps_code} from the dashboard? It won't be re-imported from PSDoc.`)) return;
    try {
      await api.post(`/jobs/${j.id}/dismiss`);
      toast.success(`Removed ${j.ps_code}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to remove job"); }
  };

  const handleStartCO = (co) => {
    if (co.status !== "open") {
      // already generated → load the delta config for review
      if (co.gen_id) {
        api.get(`/history/${co.gen_id}`).then(({ data }) => { onLoad(data); onOpenChange(false); })
          .catch(() => toast.error("Failed to load delta config"));
        return;
      }
    }
    if (!co.project_known) {
      toast.error("No project exists yet for this PS code — generate the base config first.");
      return;
    }
    onStartCO(co);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="jobs-dialog" className="max-w-2xl bg-zinc-950 border-zinc-800 text-zinc-200">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><BriefcaseIcon className="h-5 w-5 text-cyan-400" /> Design jobs</DialogTitle>
          <DialogDescription className="text-zinc-500">Approved SOWs from PSDoc. Accept a job, generate the config, then sign off (tested → complete) to send it back for deployment.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-1">
          {loading && <p className="text-sm text-zinc-500 py-8 text-center">Loading…</p>}
          {!loading && items.length === 0 && <p className="text-sm text-zinc-600 py-8 text-center">No jobs yet. Approved SOWs from PSDoc will appear here.</p>}
          {items.map((j) => {
            const st = STATUS[j.status] || STATUS.new;
            const action = ACTION[j.status];
            return (
              <div key={j.id} data-testid={`job-item-${j.id}`}
                className={`rounded-sm border border-zinc-800 bg-zinc-900/50 px-4 py-3 ${action ? "hover:bg-zinc-900 hover:border-zinc-700 cursor-pointer" : "opacity-80"}`}
                onClick={() => { if (action) handleOpen(j); }}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="inline-flex items-center gap-1 font-mono text-sm text-cyan-300"><HashIcon className="h-3.5 w-3.5" />{j.ps_code}</span>
                      <span data-testid={`job-status-${j.id}`} className={`rounded-sm border px-1.5 py-0.5 text-[10px] ${st.cls}`}>{st.label}</span>
                    </div>
                    <p className="flex items-center gap-1 text-xs text-zinc-400"><UserIcon className="h-3 w-3 text-zinc-600" /> {j.approval_name || "—"} · {j.client || "—"}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-zinc-600">
                      {(j.documents || []).map((d, i) => (<span key={i} className="inline-flex items-center gap-1"><FileTextIcon className="h-3 w-3" /> {d.kind}</span>))}
                      {j.vendor_hint?.vendors?.length ? <span>· {j.vendor_hint.vendors.join("/")}</span> : null}
                      <span>· {j.created_at ? new Date(j.created_at).toLocaleDateString() : ""}</span>
                    </div>
                  </div>
                  {action ? (
                    <span data-testid={`job-action-${j.id}`} className="inline-flex shrink-0 items-center gap-1 rounded-sm bg-cyan-500 px-2 py-1 text-[11px] font-semibold text-zinc-950"><PlayIcon weight="fill" className="h-3 w-3" /> {action}</span>
                  ) : (
                    <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-emerald-400"><CheckCircleIcon weight="fill" className="h-3.5 w-3.5" /> done</span>
                  )}
                </div>
                <div className="mt-2 flex justify-end gap-2">
                  {canReopen(j.status) && (
                    <button data-testid={`job-reopen-${j.id}`} onClick={(e) => handleReopen(e, j)}
                      className="inline-flex items-center gap-1 rounded-sm border border-zinc-700 px-2 py-0.5 text-[10px] text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200">
                      <ArrowUUpLeftIcon className="h-3 w-3" /> Send back a step
                    </button>
                  )}
                  <button data-testid={`job-dismiss-${j.id}`} onClick={(e) => handleDismiss(e, j)}
                    className="inline-flex items-center gap-1 rounded-sm border border-zinc-800 px-2 py-0.5 text-[10px] text-zinc-500 hover:border-rose-500/40 hover:bg-rose-500/10 hover:text-rose-300">
                    <TrashIcon className="h-3 w-3" /> Remove
                  </button>
                </div>
              </div>
            );
          })}

          {cos.length > 0 && (
            <div className="pt-4" data-testid="signed-co-section">
              <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-400">
                <GitDiffIcon className="h-3.5 w-3.5 text-cyan-400" /> Signed change orders (PSDoc)
              </p>
              <div className="space-y-2">
                {cos.map((co) => {
                  const st = STATUS[co.status] || STATUS.open;
                  const actionable = co.status === "open";
                  return (
                    <div key={co.id} data-testid={`co-item-${co.id}`}
                      className="rounded-sm border border-zinc-800 bg-zinc-900/50 px-4 py-3 hover:bg-zinc-900 hover:border-zinc-700 cursor-pointer"
                      onClick={() => handleStartCO(co)}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="inline-flex items-center gap-1 font-mono text-sm text-cyan-300"><HashIcon className="h-3.5 w-3.5" />{co.change_code}</span>
                            <span data-testid={`co-status-${co.id}`} className={`rounded-sm border px-1.5 py-0.5 text-[10px] ${st.cls}`}>{st.label}</span>
                            {!co.project_known && <span className="rounded-sm border border-rose-500/40 bg-rose-500/10 px-1.5 py-0.5 text-[10px] text-rose-300">no base project</span>}
                          </div>
                          <p className="text-xs text-zinc-300 truncate">{co.title || "Change order"}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-zinc-600">
                            {co.customer && <span className="inline-flex items-center gap-1"><UserIcon className="h-3 w-3" /> {co.customer}</span>}
                            {co.flavour && <span>· {co.flavour}</span>}
                            <span>· {co.created_at ? new Date(co.created_at).toLocaleDateString() : ""}</span>
                          </div>
                        </div>
                        {actionable && co.project_known ? (
                          <span data-testid={`co-action-${co.id}`} className="inline-flex shrink-0 items-center gap-1 rounded-sm bg-cyan-500 px-2 py-1 text-[11px] font-semibold text-zinc-950"><PlayIcon weight="fill" className="h-3 w-3" /> Generate delta</span>
                        ) : actionable && !co.project_known ? (
                          <span className="inline-flex shrink-0 items-center gap-1 rounded-sm border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-[11px] text-rose-300">needs base config</span>
                        ) : co.gen_id ? (
                          <span className="inline-flex shrink-0 items-center gap-1 rounded-sm border border-violet-500/40 bg-violet-500/10 px-2 py-1 text-[11px] text-violet-300"><FileTextIcon className="h-3 w-3" /> View delta</span>
                        ) : (
                          <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-emerald-400"><CheckCircleIcon weight="fill" className="h-3.5 w-3.5" /> done</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
